import React, { useState, useEffect } from 'react';
import { 
  Users, 
  Calendar, 
  TrendingUp, 
  X,
  BarChart3,
  PieChart,
  UserCheck
} from 'lucide-react';
import './MostreDashboard.scss';

const MostreDashboard = ({ 
  isOpen, 
  onClose, 
  allAppointments = [],
  userNotes = {} 
}) => {
  const [selectedTimeRange, setSelectedTimeRange] = useState('all');
  const [filteredData, setFilteredData] = useState([]);
  const [cityStats, setCityStats] = useState([]);
  const [campaignStats, setCampaignStats] = useState([]);
  const [hoveredSegment, setHoveredSegment] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  // Time range options
  const timeRangeOptions = [
    { value: 'all', label: 'Toate datele' },
    { value: '1week', label: 'Ultima săptămână' },
    { value: '1month', label: 'Ultima lună' },
    { value: '3months', label: 'Ultimele 3 luni' },
    { value: '6months', label: 'Ultimele 6 luni' },
    { value: '12months', label: 'Ultimul an' }
  ];

  // Filter data based on time range
  useEffect(() => {
    if (!allAppointments.length) return;

    let filtered = [...allAppointments];
    const now = new Date();
    
    if (selectedTimeRange !== 'all') {
      const timeMap = {
        '1week': 7,
        '1month': 30,
        '3months': 90,
        '6months': 180,
        '12months': 365
      };
      
      const daysBack = timeMap[selectedTimeRange];
      const cutoffDate = new Date(now.getTime() - (daysBack * 24 * 60 * 60 * 1000));
      
      filtered = allAppointments.filter(appointment => {
        // Try different date field names that might exist
        const appointmentDate = appointment.createDate 
          ? new Date(appointment.createDate)
          : appointment.createdAt 
          ? new Date(appointment.createdAt)
          : appointment.date
          ? new Date(appointment.date)
          : null;
          
        if (!appointmentDate || isNaN(appointmentDate.getTime())) {
          return false; // Exclude appointments without valid dates
        }
        
        return appointmentDate >= cutoffDate;
      });
    }

    setFilteredData(filtered);
  }, [allAppointments, selectedTimeRange]);

  // Calculate city statistics
  useEffect(() => {
    if (!filteredData.length) return;

    const cityMap = {};
    filteredData.forEach(appointment => {
      const city = appointment.city || 'Necunoscut';
      if (!cityMap[city]) {
        cityMap[city] = { total: 0, enrolled: 0 };
      }
      cityMap[city].total++;
      
      const key = `${appointment.campaignId}_${appointment.id}`;
      if (userNotes[key]?.inscris) {
        cityMap[city].enrolled++;
      }
    });

    const cityStatsArray = Object.entries(cityMap)
      .map(([city, stats]) => ({
        city,
        total: stats.total,
        enrolled: stats.enrolled,
        percentage: stats.total > 0 ? ((stats.enrolled / stats.total) * 100).toFixed(1) : '0'
      }))
      .sort((a, b) => b.total - a.total);

    setCityStats(cityStatsArray);
  }, [filteredData, userNotes]);

  // Prepare pie chart data and colors
  const pieChartData = cityStats.slice(0, 6).map((city, index) => ({
    name: city.city,
    value: city.total,
    percentage: city.percentage
  }));

  const COLORS = [
    '#904d71', '#b8658a', '#d67ea3', '#e899b7', 
    '#f4b4cb', '#c66487'
  ];

  // Calculate pie chart segments
  const calculatePieSegments = () => {
    let cumulativePercentage = 0;
    return pieChartData.map((item, index) => {
      const percentage = (item.value / filteredData.length) * 100;
      const startAngle = cumulativePercentage * 3.6; // Convert to degrees
      cumulativePercentage += percentage;
      const endAngle = cumulativePercentage * 3.6;
      
      return {
        ...item,
        startAngle,
        endAngle,
        percentage: percentage.toFixed(1),
        color: COLORS[index % COLORS.length]
      };
    });
  };

  const pieSegments = calculatePieSegments();

  // Calculate campaign statistics
  useEffect(() => {
    if (!filteredData.length) return;

    const campaignMap = {};
    filteredData.forEach(appointment => {
      const campaign = appointment.campaignName || 'Necunoscut';
      if (!campaignMap[campaign]) {
        campaignMap[campaign] = { total: 0, enrolled: 0 };
      }
      campaignMap[campaign].total++;
      
      const key = `${appointment.campaignId}_${appointment.id}`;
      if (userNotes[key]?.inscris) {
        campaignMap[campaign].enrolled++;
      }
    });

    const campaignStatsArray = Object.entries(campaignMap)
      .map(([campaign, stats]) => ({
        campaign,
        total: stats.total,
        enrolled: stats.enrolled,
        percentage: stats.total > 0 ? ((stats.enrolled / stats.total) * 100).toFixed(1) : '0'
      }))
      .sort((a, b) => b.total - a.total);

    setCampaignStats(campaignStatsArray);
  }, [filteredData, userNotes]);

  // Calculate total statistics
  const getTotalStats = () => {
    const totalPersons = filteredData.length;
    const enrolledCount = filteredData.filter(appointment => {
      const key = `${appointment.campaignId}_${appointment.id}`;
      return userNotes[key]?.inscris;
    }).length;
    
    const enrollmentRate = totalPersons > 0 ? ((enrolledCount / totalPersons) * 100).toFixed(1) : '0';
    
    return { totalPersons, enrolledCount, enrollmentRate };
  };

  const { totalPersons, enrolledCount, enrollmentRate } = getTotalStats();

  if (!isOpen) return null;

  return (
    <div className="dashboard-modal-overlay" onClick={onClose}>
      <div className="dashboard-modal-content" onClick={e => e.stopPropagation()}>
        <div className="dashboard-header" style={{paddingLeft: '10px', paddingTop: '10px', paddingBottom: '10px', background: 'linear-gradient(90deg, #a86a8d 0%, #904d71 100%)', borderTopLeftRadius: '12px', borderTopRightRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
          <h2 className="dashboard-title" style={{fontSize: '2rem', fontWeight: 700, color: 'white', display: 'flex', alignItems: 'center', gap: '10px'}}>
            <BarChart3 size={24} />
            Dashboard Mostre
          </h2>
          <button className="close-button" style={{
            padding: '0',
            width: '56px',
            height: '56px',
            background: 'none',
            border: 'none',
            borderRadius: '0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'background 0.2s',
          }}
            onClick={onClose}
            aria-label="Închide dashboard"
          >
            <span style={{fontSize: '2.6rem', color: 'white', fontWeight: 400, lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Arial, Helvetica, sans-serif'}}>&times;</span>
          </button>
        </div>

        <div className="dashboard-body">
          {/* Time Range Filter - now inline and compact */}
          <div style={{display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: '10px'}}>
            <span style={{display: 'flex', alignItems: 'center', fontSize: '13px', fontWeight: 500, color: '#374151', marginRight: '6px'}}>
              <Calendar size={14} style={{marginRight: '4px'}} /> Perioada de timp:
            </span>
            <select 
              value={selectedTimeRange} 
              onChange={(e) => setSelectedTimeRange(e.target.value)}
              style={{padding: '4px 10px', borderRadius: '8px', fontSize: '13px', minWidth: '120px', height: '28px', border: '1px solid #e5e7eb', background: 'white'}}
            >
              {timeRangeOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Overview Stats */}
          <div className="stats-overview" style={{marginBottom: '10px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'}}>
            <h3 style={{marginBottom: '12px', fontSize: '22px'}}>Perspective rapide</h3>
            <div className="stats-cards-container" style={{
              display: 'flex', 
              flexDirection: window.innerWidth <= 767 ? 'column' : 'row', 
              gap: window.innerWidth <= 767 ? '18px' : '32px', 
              justifyContent: 'center', 
              alignItems: 'center', 
              width: '100%',
              maxWidth: window.innerWidth <= 767 ? '100%' : '540px',
              padding: '20px 0',
              boxSizing: 'border-box',
            }}>
              {[{
                className: 'total-persons',
                icon: <Users size={24} style={{width: '32px', height: '32px'}} />, 
                number: totalPersons, 
                color: '#2563eb', 
                label: 'TOTAL PERSOANE'
              }, {
                className: 'enrolled-persons',
                icon: <UserCheck size={24} style={{width: '32px', height: '32px'}} />, 
                number: enrolledCount, 
                color: '#16a34a', 
                label: 'ÎNSCRIȘI'
              }, {
                className: 'enrollment-rate',
                icon: <TrendingUp size={24} style={{width: '32px', height: '32px'}} />, 
                number: `${enrollmentRate}%`, 
                color: '#d97706', 
                label: 'RATA ÎNCRIERE'
              }].map((stat, idx) => (
                <div key={stat.className} className={`stat-card ${stat.className}`} style={{
                  flex: window.innerWidth <= 767 ? 'none' : 1, 
                  minWidth: window.innerWidth <= 767 ? '280px' : '170px', 
                  width: window.innerWidth <= 767 ? '100%' : 'auto',
                  minHeight: '80px', 
                  height: '140px', 
                  padding: '24px 18px', 
                  display: 'flex', 
                  flexDirection: 'row', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  boxShadow: '0 4px 16px rgba(144,77,113,0.10)', 
                  background: 'rgba(255,255,255,0.95)', 
                  borderRadius: '16px', 
                  backdropFilter: 'blur(12px)'
                }}>
                  <div className="stat-icon" style={{padding: '2px', minWidth: '32px', minHeight: '32px', marginRight: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: idx === 0 ? '#e6edfa' : idx === 1 ? '#e6faed' : '#faefd6', borderRadius: '12px', height: '60px', width: '48px'}}>
                    {stat.icon}
                  </div>
                  <div className="stat-content" style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center', width: '100%'}}>
                    <div className="stat-number" style={{fontSize: '28px', marginBottom: '8px', fontWeight: 700, letterSpacing: '0.02em', color: stat.color}}> {stat.number} </div>
                    <div className="stat-label" style={{fontSize: '16px', color: '#6b7280', fontWeight: 500, letterSpacing: '0.01em'}}>{stat.label}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Charts Section */}
          <div className="charts-section" style={{
  display: 'flex', 
  gap: '10px', 
  marginBottom: '10px',
  flexDirection: 'row',
  flexWrap: 'wrap',
  ...(window.innerWidth <= 767 ? {
    flexDirection: 'column',
    gap: '0',
  } : {})
}}>
            {/* City Statistics - Pie Chart */}
            <div className="chart-container" style={{flex: 1}}>
              <div className="chart-header">
                <PieChart size={18} />
                <h3>Distribuția pe orașe</h3>
              </div>
              <div className="chart-content">
                {pieChartData.length > 0 ? (
                  <div className="pie-chart-container" style={{position: 'relative'}}>
                    {/* SVG Pie Chart */}
                    <div className="svg-pie-chart">
                      <svg width="240" height="240" viewBox="0 0 240 240">
                        <circle cx="120" cy="120" r="100" fill="none" stroke="#e5e7eb" strokeWidth="2"/>
                        {pieSegments.map((segment, index) => {
                          const { startAngle, endAngle, color } = segment;
                          const largeArcFlag = (endAngle - startAngle) > 180 ? 1 : 0;
                          const startAngleRad = (startAngle * Math.PI) / 180;
                          const endAngleRad = (endAngle * Math.PI) / 180;
                          const x1 = 120 + 90 * Math.cos(startAngleRad);
                          const y1 = 120 + 90 * Math.sin(startAngleRad);
                          const x2 = 120 + 90 * Math.cos(endAngleRad);
                          const y2 = 120 + 90 * Math.sin(endAngleRad);
                          const pathData = [
                            `M 120 120`,
                            `L ${x1} ${y1}`,
                            `A 90 90 0 ${largeArcFlag} 1 ${x2} ${y2}`,
                            `Z`
                          ].join(' ');
                          return (
                            <path
                              key={index}
                              d={pathData}
                              fill={color}
                              stroke="white"
                              strokeWidth="2"
                              className="pie-segment"
                              onMouseEnter={(e) => {
                                setHoveredSegment(segment);
                                const rect = e.currentTarget.getBoundingClientRect();
                                setTooltipPosition({ x: rect.x + rect.width / 2, y: rect.y });
                              }}
                              onMouseLeave={() => {
                                setHoveredSegment(null);
                              }}
                              style={{
                                cursor: 'pointer',
                                filter: hoveredSegment?.name === segment.name ? 'brightness(1.1)' : 'none',
                                transform: hoveredSegment?.name === segment.name ? 'scale(1.02)' : 'scale(1)',
                                transformOrigin: '120px 120px',
                                transition: 'all 0.2s ease'
                              }}
                            />
                          );
                        })}
                        <circle cx="120" cy="120" r="45" fill="white" stroke="#e5e7eb" strokeWidth="2"/>
                        <text x="120" y="115" textAnchor="middle" className="pie-total-text" style={{fontSize: '22px'}}>
                          {filteredData.length}
                        </text>
                        <text x="120" y="130" textAnchor="middle" className="pie-label-text" style={{fontSize: '13px'}}>
                          Total
                        </text>
                        {/* Show city and count inline only, no black background tooltip */}
                        {hoveredSegment && (
                          <text x="120" y="155" textAnchor="middle" style={{fontSize: '11px', fontWeight: 500, fill: '#374151'}}>
                            {hoveredSegment.name}: {hoveredSegment.value}
                          </text>
                        )}
                      </svg>
                      {/* Removed the black background tooltip and the city count display with background color */}
                    </div>
                    {/* City stats summary below chart */}
                    <div className="city-stats-summary">
                      {cityStats.slice(0, 6).map((cityData, index) => (
                        <div key={cityData.city} className="city-summary-item">
                          <div 
                            className="city-color-indicator" 
                            style={{ backgroundColor: COLORS[index % COLORS.length] }}
                          />
                          <span className="city-name">{cityData.city}</span>
                          <span className="city-count">{cityData.total}</span>
                          <span className="city-percentage">{((cityData.total / filteredData.length) * 100).toFixed(1)}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="no-data">Nu există date pentru perioada selectată</div>
                )}
              </div>
            </div>
            {/* Statistici pe campanii - move below for mobile */}
            {window.innerWidth > 767 ? (
              <div className="chart-container" style={{flex: 1, height: 'auto', minHeight: 'unset'}}>
                <div className="chart-header">
                  <PieChart size={18} />
                  <h3>Statistici pe campanii</h3>
                </div>
                <div 
                  className="chart-content"
                  style={{ '--campaign-count': campaignStats.length, minHeight: 'unset', height: 'auto', paddingBottom: '0', paddingTop: campaignStats.length > 1 ? '18px' : '8px', maxHeight: campaignStats.length > 2 ? 'none' : '180px', overflowY: 'auto' }}
                >
                  {campaignStats.length > 0 ? (
                    <div className="stats-list">
                      {campaignStats.map((campaignData, index) => (
                        <div key={campaignData.campaign} className="stat-item" style={{padding: '10px 0'}}>
                          <div className="stat-info">
                            <span className="stat-name">{campaignData.campaign}</span>
                            <span className="stat-details">
                              {campaignData.enrolled}/{campaignData.total} ({campaignData.percentage}%)
                            </span>
                          </div>
                          <div className="stat-bar">
                            <div 
                              className="stat-progress campaign" 
                              style={{ width: `${campaignData.percentage}%` }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="no-data">Nu există date pentru perioada selectată</div>
                  )}
                </div>
              </div>
            ) : null}
          </div>
          {window.innerWidth <= 767 ? (
  <div className="chart-container" style={{width: '100%', marginTop: '10px', height: 'auto', minHeight: 'unset'}}>
    {/* Statistici pe campanii for mobile */}
    <div className="chart-header">
      <PieChart size={18} />
      <h3>Statistici pe campanii</h3>
    </div>
    <div 
      className="chart-content"
      style={{ '--campaign-count': campaignStats.length, minHeight: 'unset', height: 'auto', paddingBottom: '0', paddingTop: campaignStats.length > 1 ? '18px' : '8px', maxHeight: campaignStats.length > 2 ? 'none' : '180px', overflowY: 'auto' }}
    >
      {campaignStats.length > 0 ? (
        <div className="stats-list">
          {campaignStats.map((campaignData, index) => (
            <div key={campaignData.campaign} className="stat-item" style={{padding: '10px 0'}}>
              <div className="stat-info">
                <span className="stat-name">{campaignData.campaign}</span>
                <span className="stat-details">
                  {campaignData.enrolled}/{campaignData.total} ({campaignData.percentage}%)
                </span>
              </div>
              <div className="stat-bar">
                <div 
                  className="stat-progress campaign" 
                  style={{ width: `${campaignData.percentage}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="no-data">Nu există date pentru perioada selectată</div>
      )}
    </div>
  </div>
) : null}

          {/* Additional Insights */}
          <div className="insights-section" style={{marginBottom: '10px'}}>
            <div className="insights-header">
              <TrendingUp size={18} />
              <h3 style={{fontSize: '20px', fontWeight: 500, letterSpacing: '0.02em', color: '#904d71'}}>Informații suplimentare</h3>
            </div>
            <div className="insights-grid" style={{gap: '18px', padding: '24px', borderRadius: '20px', background: 'rgba(255,255,255,0.98)', backdropFilter: 'blur(20px)', boxShadow: '0 8px 32px rgba(144,77,113,0.10)', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))'}}>
              <div className="insight-item" style={{fontSize: '17px', fontWeight: 400, color: '#374151', letterSpacing: '0.01em', background: 'rgba(255,255,255,0.7)', borderRadius: '12px', padding: '12px 18px'}}>
                <span style={{fontSize: '17px', color: '#904d71'}}>Cel mai activ oraș:</span> <span style={{marginLeft: '6px'}}>{cityStats.length > 0 ? cityStats[0].city : 'N/A'}</span>
              </div>
              <div className="insight-item" style={{fontSize: '17px', fontWeight: 400, color: '#374151', letterSpacing: '0.01em', background: 'rgba(255,255,255,0.7)', borderRadius: '12px', padding: '12px 18px'}}>
                <span style={{fontSize: '17px', color: '#904d71'}}>Campania cu cea mai mare rată:</span> <span style={{marginLeft: '6px'}}>{campaignStats.length > 0 
                  ? `${campaignStats.sort((a, b) => parseFloat(b.percentage) - parseFloat(a.percentage))[0].campaign} (${campaignStats.sort((a, b) => parseFloat(b.percentage) - parseFloat(a.percentage))[0].percentage}%)`
                  : 'N/A'
                }</span>
              </div>
              <div className="insight-item" style={{fontSize: '17px', fontWeight: 400, color: '#374151', letterSpacing: '0.01em', background: 'rgba(255,255,255,0.7)', borderRadius: '12px', padding: '12px 18px'}}>
                <span style={{fontSize: '17px', color: '#904d71'}}>Total orașe:</span> <span style={{marginLeft: '6px'}}>{cityStats.length}</span>
              </div>
              <div className="insight-item" style={{fontSize: '17px', fontWeight: 400, color: '#374151', letterSpacing: '0.01em', background: 'rgba(255,255,255,0.7)', borderRadius: '12px', padding: '12px 18px'}}>
                <span style={{fontSize: '17px', color: '#904d71'}}>Total campanii:</span> <span style={{marginLeft: '6px'}}>{campaignStats.length}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MostreDashboard;

/* Add responsive styles for stat cards and city summary */
<style>{`
  @media (max-width: 767px) {
    .stats-cards-container {
      flex-direction: column !important;
      gap: 18px !important;
      max-width: 100% !important;
    }
    .stat-card {
      min-width: 0 !important;
      width: 100% !important;
      margin-bottom: 0 !important;
      height: auto !important;
    }
    .charts-section {
      flex-direction: column !important;
      gap: 0 !important;
    }
    .chart-container {
      width: 100% !important;
      margin-bottom: 10px !important;
    }
    .city-stats-summary {
      flex-direction: column !important;
      gap: 8px !important;
    }
  }
`}</style>
